/**
 * UserSettingsService
 * Centralized management of user preferences with multi-tier storage scopes.
 */

import { Logger } from './Logger';

const logger = Logger.scope('UserSettings');

export enum SettingScope {
  SESSION = 1, // sessionStorage (Tab lifetime)
  LOCAL = 2,   // localStorage (Device lifetime)
  REMOTE = 4,  // Web API (User lifetime)
  ALL = 7
}

export interface SettingDefinition<T> {
  key: string;
  defaultValue: T;
  scope: SettingScope;
  sync?: boolean;
}

type ChangeCallback<T> = (value: T) => void;

class UserSettingsService {
  private listeners: Map<string, Set<ChangeCallback<any>>> = new Map();
  private memoryCache: Map<string, any> = new Map();

  constructor() {
    logger.debug('Initializing UserSettingsService');
    // We could load all local settings into memory here if needed, 
    // but for now we'll read on demand to ensure freshness.
    this.syncRemoteSettings();
  }

  /**
   * Mock synchronization of remote settings
   */
  private async syncRemoteSettings(): Promise<void> {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    logger.info('[Mock Remote Sync] Remote settings synchronized');
  }

  /**
   * Get a setting value.
   * Checks scopes in order: SESSION -> LOCAL -> REMOTE (cached) -> Default (if provided)
   */
  public get<T>(key: string, defaultValue?: T): T | undefined {
    // 1. Check Session
    const sessionValue = this.readFromStorage(sessionStorage, key);
    if (sessionValue !== undefined) return sessionValue as T;

    // 2. Check Local
    const localValue = this.readFromStorage(localStorage, key);
    if (localValue !== undefined) return localValue as T;

    // 3. Check Remote (TODO: Implement remote sync/cache)
    // For now, remote settings might be cached in memory or local storage
    
    return defaultValue;
  }

  /**
   * Set a setting value.
   * @param key The setting key
   * @param value The value to store
   * @param scope The scope to store in (default: LOCAL)
   */
  public async set<T>(key: string, value: T, scope: SettingScope = SettingScope.LOCAL): Promise<void> {
    try {
      // Handle SESSION scope
      if (scope & SettingScope.SESSION) {
        this.writeToStorage(sessionStorage, key, value);
      }

      // Handle LOCAL scope
      if (scope & SettingScope.LOCAL) {
        this.writeToStorage(localStorage, key, value);
      }

      // Handle REMOTE scope
      if (scope & SettingScope.REMOTE) {
        // Optimistic update: Save to local cache immediately
        this.writeToStorage(localStorage, key, value); 
        
        // Mock push to backend
        this.pushToRemote(key, value).catch(err => {
          logger.error(`[Mock Remote Sync] Failed to push ${key}`, err);
          // TODO: Implement retry queue
        });
      }

      // Update memory cache
      this.memoryCache.set(key, value);

      // Notify listeners
      this.notifyListeners(key, value);

    } catch (error) {
      logger.error(`Failed to set setting ${key}:`, error);
      throw error;
    }
  }

  /**
   * Remove a setting from specified scope(s)
   */
  public remove(key: string, scope: SettingScope = SettingScope.ALL): void {
    if (scope & SettingScope.SESSION) {
      sessionStorage.removeItem(key);
    }
    if (scope & SettingScope.LOCAL) {
      localStorage.removeItem(key);
    }
    // TODO: Handle remote removal
    
    this.memoryCache.delete(key);
    this.notifyListeners(key, undefined);
  }

  /**
   * Subscribe to changes for a specific setting key
   */
  public onChange<T>(key: string, callback: ChangeCallback<T>): void {
    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set());
    }
    this.listeners.get(key)!.add(callback);
  }

  /**
   * Unsubscribe from changes
   */
  public off<T>(key: string, callback: ChangeCallback<T>): void {
    const keyListeners = this.listeners.get(key);
    if (keyListeners) {
      keyListeners.delete(callback);
      if (keyListeners.size === 0) {
        this.listeners.delete(key);
      }
    }
  }

  /**
   * Get all stored settings
   */
  public getAll(): Array<{ key: string, value: any, scope: string }> {
    const settings: Array<{ key: string, value: any, scope: string }> = [];
    const keys = new Set<string>();

    // Read Session Storage
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key) {
        keys.add(key);
        settings.push({
          key: key,
          value: this.readFromStorage(sessionStorage, key),
          scope: 'SESSION'
        });
      }
    }

    // Read Local Storage
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        if (!keys.has(key)) {
           settings.push({
            key: key,
            value: this.readFromStorage(localStorage, key),
            scope: 'LOCAL'
          });
        } else {
            const existing = settings.find(s => s.key === key);
            if (existing) {
                existing.scope = 'SESSION (Overrides LOCAL)';
            }
        }
      }
    }
    
    return settings;
  }

  // --- Private Helpers ---

  private readFromStorage(storage: Storage, key: string): any | undefined {
    try {
      const item = storage.getItem(key);
      if (item === null) return undefined;

      // Try parsing JSON, if it fails return string
      try {
        return JSON.parse(item);
      } catch {
        return item;
      }
    } catch (e) {
      logger.warn(`Error reading ${key} from storage`, e);
      return undefined;
    }
  }

  private writeToStorage(storage: Storage, key: string, value: any): void {
    try {
      const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
      storage.setItem(key, stringValue);
    } catch (e) {
      logger.error(`Error writing ${key} to storage`, e);
    }
  }

  private notifyListeners(key: string, value: any): void {
    const keyListeners = this.listeners.get(key);
    if (keyListeners) {
      keyListeners.forEach(callback => {
        try {
          callback(value);
        } catch (e) {
          logger.error(`Error in listener for ${key}`, e);
        }
      });
    }
  }

  private async pushToRemote(key: string, value: any): Promise<void> {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 500));
    logger.debug(`[Mock Remote Sync] Pushed ${key} to backend`);
  }
}

export const userSettingsService = new UserSettingsService();
